import React, { useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Select,
} from '@chakra-ui/react';
import { SaktiEstateOption, SaktiFilterOptions, SaktiFilters } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

interface SaktiFilterBarProps {
  filters: SaktiFilters;
  options: SaktiFilterOptions;
  onFiltersChange: (nextFilters: SaktiFilters) => void;
  onApply: () => void;
  isApplying?: boolean;
}

const SaktiFilterBar: React.FC<SaktiFilterBarProps> = ({
  filters,
  options,
  onFiltersChange,
  onApply,
  isApplying = false,
}) => {
  const filteredEstates = useMemo(() => {
    if (!filters.company_id) {
      return options.estates;
    }
    return options.estates.filter((estate) => estate.company_id === filters.company_id);
  }, [filters.company_id, options.estates]);

  const filteredDivisions = useMemo(() => {
    if (!filters.estate_id) {
      return [];
    }

    const selectedEstate = options.estates.find((estate) => estate.odoo_id === filters.estate_id);
    if (!selectedEstate) {
      return [];
    }

    return options.divisions.filter((division) => division.estate_id === selectedEstate.id);
  }, [filters.estate_id, options.divisions, options.estates]);

  const onCompanyChange = (value: string) => {
    const companyId = value ? Number(value) : undefined;
    onFiltersChange({
      ...filters,
      company_id: companyId,
      estate_id: undefined,
      division_id: undefined,
    });
  };

  const onEstateChange = (value: string) => {
    const estateId = value ? Number(value) : undefined;
    onFiltersChange({
      ...filters,
      estate_id: estateId,
      division_id: undefined,
    });
  };

  const onDivisionChange = (value: string) => {
    const divisionId = value ? Number(value) : undefined;
    onFiltersChange({
      ...filters,
      division_id: divisionId,
    });
  };

  return (
    <Box
      bg="white"
      borderRadius={SAKTI_UI_SIZE.cardRadius}
      borderWidth="1px"
      borderColor="gray.200"
      p={SAKTI_UI_SIZE.cardPadding}
    >
      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: '1.4fr 1.2fr 1.2fr auto' }}
        gap={SAKTI_UI_SIZE.filterGridGap}
        alignItems="end"
      >
        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            COMPANY
          </FormLabel>
          <Select
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            bg="white"
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            value={filters.company_id ?? ''}
            placeholder="Pilih company"
            onChange={(event) => onCompanyChange(event.target.value)}
          >
            {options.companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            ESTATE
          </FormLabel>
          <Select
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            bg="white"
            value={filters.estate_id ?? ''}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            placeholder="Pilih estate"
            onChange={(event) => onEstateChange(event.target.value)}
          >
            {filteredEstates.map((estate: SaktiEstateOption) => (
              <option key={estate.id} value={estate.odoo_id}>
                {estate.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            DIVISION
          </FormLabel>
          <Select
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            bg="white"
            value={filters.division_id ?? ''}
            placeholder="Pilih divisi"
            isDisabled={!filters.estate_id}
            onChange={(event) => onDivisionChange(event.target.value)}
          >
            {filteredDivisions.map((division) => (
              <option key={division.id} value={division.odoo_id}>
                {division.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <Button
          size="sm"
          h={SAKTI_UI_SIZE.controlHeight}
          w={{ base: 'full', xl: 'auto' }}
          px={5}
          isLoading={isApplying}
          loadingText="Applying"
          onClick={onApply}
          bg="customGreen.500"
          _hover={{ bg: 'customGreen.600' }}
          color="white"
          mt={{ base: 2, xl: 0 }}
        >
          Apply Filter
        </Button>
      </Grid>
    </Box>
  );
};

export default SaktiFilterBar;
